import { AutoCompleteItem } from 'lib/types/AutoCompleteInput'
import { useCallback, useEffect, useRef, useState } from 'react'

import { IconNames } from 'lib/settings/icons'

import { Club } from '@/types/autoComplete'
import { CamelToSnakeCase } from '@/utils/casingHelpers'
import { resolveStreetLine, type StreetPrediction } from '@/utils/placeStreet'

import useAutoCompleteApi, { mapIconURL } from '../api/useAutoCompleteApi'
import { usePlaceDetailsApi } from '../api/usePlaceDetailsApi'

/**
 * The autocomplete payload carries more than the component-library type
 * declares — `structured_formatting` among it, which is the only field holding
 * the full prediction line (`name` can arrive truncated to the matched span).
 */
export type SelectableAutoCompleteItem = AutoCompleteItem & StreetPrediction

export interface UseAutoCompleteAddress {
  address: AutoCompleteAddress | null
  addressError: Error | null
  autoCompleteError: Error | null
  autoCompleteItems: AutoCompleteItem[]
  isLoadingAddress: boolean
  isLoadingAutoComplete: boolean
  onInputSearchTerm: (text: string) => void
  onSelectAutoCompleteItem: (item: SelectableAutoCompleteItem) => void
  recognizedPlace: RecognizedPlace | null
  searchTerm: string
}

export interface AutoCompleteAddress {
  address_1: string
  address_2: string
  attention_name: string
  city: string
  company_name: string
  country_code: string
  formatted_address: string
  phone_number: string
  state: string
  zip: string
  address_type?: string
}

export interface RecognizedPlace {
  id?: string
  icon: IconNames
  iconClassName: string
  label: string
  name: string
  showAddress2: boolean
  questionnaireEnabled?: boolean
}

export const useAutoCompleteAddress = (
  countryCode: string | undefined = undefined,
): UseAutoCompleteAddress => {
  const [address, setAddress] = useState<AutoCompleteAddress | null>(null)
  const [formattedAddress, setFormattedAddress] = useState<string>('')
  const [googlePlaceId, setGooglePlaceId] = useState<string>('')
  const [recognizedPlace, setRecognizedPlace] = useState<RecognizedPlace | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>('')
  // The clicked Google prediction, kept alongside its place id so the street
  // line can fall back to the text the visitor saw (GSE-12408).
  const [selectedPlace, setSelectedPlace] = useState<StreetPrediction | null>(null)
  // Bumped on every pick. `clearPrevious` blanks the address, and the effect
  // below is what refills it — but that effect is keyed on values a repeat pick
  // does not change, so without this the address stayed blank whenever the same
  // suggestion was chosen twice (the dropdown hands back the SAME object, so
  // even `selectedPlace` is unchanged).
  const [selectionSeq, setSelectionSeq] = useState(0)

  const {
    data: autoCompleteItems = [],
    error: autoCompleteError,
    isLoading: isLoadingAutoComplete,
  } = useAutoCompleteApi({ term: searchTerm, countryCode })

  const {
    data: placeDetails = null,
    error: addressError,
    isLoading: isLoadingAddress,
  } = usePlaceDetailsApi(googlePlaceId)

  const clearPrevious = (): void => {
    setAddress(null)
    setFormattedAddress('')
    setGooglePlaceId('')
    setRecognizedPlace(null)
    setSelectedPlace(null)
  }

  const onSelectAutoCompleteItem = (item: SelectableAutoCompleteItem): void => {
    clearPrevious()
    setSelectionSeq((seq) => seq + 1)

    const formattedAddress = item?.formatted_address?.replaceAll('<br>', ', ') || ''

    if (formattedAddress) setFormattedAddress(formattedAddress)
    if (item?.type === 'GooglePlace') {
      setGooglePlaceId(item?.id || '')
      setSelectedPlace(item)
    }
    if (!['Club', 'SkiResort'].includes(item?.type || '')) {
      // Handle manual entry: item has no type, just { id, name }
      if (!item?.type && item?.name) {
        setAddress({
          address_1: item.name,
          address_2: '',
          attention_name: '',
          city: '',
          company_name: '',
          country_code: countryCode || 'US',
          formatted_address: item.name,
          phone_number: '',
          state: '',
          zip: '',
        })
      }
      return
    }

    const club = item as CamelToSnakeCase<Club>

    setAddress({
      address_1: (club.address as Partial<{ address1: string }>).address1 || '',
      address_2: (club.address as Partial<{ address2: string }>).address2 || '',
      attention_name: '',
      city: club.address.city,
      company_name: club.name,
      country_code: club.address.country_code,
      formatted_address: formattedAddress,
      phone_number: '',
      state: club.address.state,
      zip: club.address.zip,
      address_type: '',
    })

    setRecognizedPlace({
      id: club.id,
      name: club.name,
      icon: mapIconURL(club.icon_url),
      iconClassName: 'w-4 h-4 md:w-6 md:h-6',
      label: item.name,
      showAddress2: !!(club.address as Partial<{ address2: string }>).address2,
      questionnaireEnabled: club.questionnaire_enabled,
    })
  }

  useEffect(() => {
    if (placeDetails)
      setAddress({
        address_1: resolveStreetLine(selectedPlace, placeDetails),
        address_2: '',
        attention_name: '',
        city: placeDetails.city,
        company_name: placeDetails?.types?.includes('establishment') ? placeDetails.name : '',
        country_code: placeDetails.countryCode,
        formatted_address: formattedAddress,
        phone_number: '',
        state: placeDetails.state,
        zip: placeDetails.zip,
      })
  }, [formattedAddress, placeDetails, selectedPlace, selectionSeq])

  useEffect(() => {
    if (searchTerm === '') clearPrevious()
  }, [searchTerm])

  // Stable debounced search term setter — avoids creating a new debounce closure
  // on every render, which previously caused orphaned timers and stale API calls.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debouncedSetSearchTerm = useCallback((term: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearchTerm(term)
      debounceRef.current = null
    }, 250)
  }, [])

  return {
    address,
    addressError,
    autoCompleteError: autoCompleteError,
    autoCompleteItems,
    isLoadingAddress,
    isLoadingAutoComplete: isLoadingAutoComplete,
    onInputSearchTerm: debouncedSetSearchTerm,
    onSelectAutoCompleteItem,
    recognizedPlace,
    searchTerm,
  }
}
